import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isVisible, setIsVisible] = useState(false);

  const PhoneLink = ({ number }) => (
    <a
      href={`tel:${number}`}
      className="block text-gray-700 hover:text-[#F5A623] transition-colors text-base font-medium flex items-center"
    >
      <span className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center mr-3 group-hover:bg-[#F5A623] group-hover:text-white transition-colors">
        <Phone className="w-3.5 h-3.5" />
      </span>
      {number}
    </a>
  );

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fill in all required fields'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        type: 'error',
        message: 'Please enter a valid email address'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      console.log('Submitting form data:', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim(),
        message: formData.message.trim(),
        source: 'Hospet Hydraulics Contact Form'
      });

      const response = await fetch('https://hospethydraulicsolutions.com/send-mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim(),
          message: formData.message.trim(),
          source: 'Hospet Hydraulics Contact Form'
        })
      });

      console.log('Response status:', response.status, response.statusText);

      let data;
      try {
        data = await response.json();
        console.log('Server response:', data);
      } catch (jsonError) {
        console.error('Error parsing JSON response:', jsonError);
        const textResponse = await response.text();
        console.log('Raw server response:', textResponse);
        throw new Error('Invalid response format from server');
      }

      if (!response.ok) {
        throw new Error(data.message || `Server responded with status: ${response.status}`);
      }

      if (data.success) {
        setSubmitStatus({
          type: 'success',
          message: data.message || 'Thank you for contacting us! We will respond within 24 hours.'
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message.includes('Failed to fetch')
          ? 'Unable to connect to the server. Please check your internet connection.'
          : error.message || 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  if (!isVisible) return null;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#00325b] to-[#0a4b8c] text-white pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h1>
            <div className="w-24 h-1.5 bg-[#F5A623] mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Have questions or need assistance? Our team is here to help. Reach out to us through any of the channels below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Section - Removed as per request */}

      {/* Contact Form & Map Section */}
      <section className="py-10 bg-gray-50">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Contact Form */}
              <div className="p-6 md:p-8">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center mb-1">
                    <MessageCircle className="w-6 h-6 text-[#F5A623] mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Send Us a Message</h2>
                  </div>
                  <p className="text-gray-600 text-sm mb-6">Fill out the form below and we'll get back to you soon.</p>

                  {submitStatus.message && (
                    <div className={`p-4 mb-6 rounded-lg ${submitStatus.type === 'success'
                      ? 'bg-green-50 text-green-800'
                      : 'bg-red-50 text-red-800'
                      }`}>
                      <div className="flex items-center">
                        {submitStatus.type === 'success' ? (
                          <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                        )}
                        <span>{submitStatus.message}</span>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label htmlFor="name" className="block text-xs font-medium text-gray-700 mb-1">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F5A623] focus:border-transparent transition-all duration-200"
                            placeholder="John Doe"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F5A623] focus:border-transparent transition-all duration-200"
                            placeholder="your@email.com"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F5A623] focus:border-transparent transition-all duration-200"
                            placeholder="+91 98765 43210"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="company" className="block text-xs font-medium text-gray-700 mb-1">
                          Company
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F5A623] focus:border-transparent transition-all duration-200"
                            placeholder="Your company name"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message" className="block text-xs font-medium text-gray-700 mb-1">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows="4"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F5A623] focus:border-transparent transition-all duration-200"
                          placeholder="How can we help you?"
                        ></textarea>
                      </div>
                    </div>

                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-[#00325b] hover:bg-[#0a4b8c] rounded-lg shadow hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F5A623] disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </motion.div>
              </div>

              {/* Map Section */}
              <div className="bg-gray-100 h-full w-full p-4 lg:p-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 h-full min-h-[400px]">
                  {/* Main Office */}
                  <motion.div
                    className="flex flex-col h-full rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex-grow">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3851.6888107936173!2d76.3823012!3d15.2601391!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bb9d7b3af92f7eb%3A0x14243a60f3a586f9!2sHospet%20Hydraulic%20Solutions!5e0!3m2!1sen!2sin!4v1718000000000!5m2!1sen!2sin"
                        width="100%"
                        height="100%"
                        style={{ border: 0, minHeight: '300px' }}
                        allowFullScreen={true}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Hospet Hydraulic Solutions - Main Office"
                        className="w-full h-full"
                      ></iframe>
                    </div>
                    <div className="bg-white p-4 text-center text-sm text-gray-600 border-t border-gray-200 flex-shrink-0">
                      <p className="font-semibold text-[#00325b] mb-1">Main Office</p>
                      <a
                        href="https://maps.google.com/?cid=1451351141381342969"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-blue-600 transition-colors block"
                      >
                        SVN Nilaya, D.No. 397/A, 31st Ward, 1st Cross, Nehru Colony, Hosapete - 583 201, Vijayanagara Dist., Karnataka
                      </a>
                    </div>
                  </motion.div>

                  {/* Workshop Location */}
                  <motion.div
                    className="flex flex-col h-full rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex-grow">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3804.228912915643!2d76.36944!3d15.2586577!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTXCsDE1JzIzLjgiTiA3NsKwMjInMjcuNCJF!5e1!3m2!1sen!2sin!4v1732745200000"
                        width="100%"
                        height="100%"
                        style={{ border: 0, minHeight: '300px' }}
                        allowFullScreen={true}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Hospet Hydraulics - Workshop"
                        className="w-full h-full"
                      ></iframe>
                    </div>
                    <div className="bg-white p-4 text-center text-sm text-gray-600 border-t border-gray-200 flex-shrink-0">
                      <p className="font-semibold text-[#00325b] mb-1">Workshop Location</p>
                      <p>Near Kampli Bypass, NH-67, Hosapete, Karnataka 583203</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Enhanced Contact Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-8 md:p-12 bg-gradient-to-br from-gray-50 to-gray-100">

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, type: 'spring', stiffness: 100 }}
                className="group relative bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#F5A623] bg-opacity-10 rounded-full transition-all duration-700 group-hover:scale-150 group-hover:opacity-20"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-[#00325b] mb-6 transform transition-transform duration-300 group-hover:scale-110">
                    <Phone className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Call Us</h3>
                  <div className="space-y-3">
                    <PhoneLink number="+91 9448145300" />
                    <PhoneLink number="+91 9448334437" />
                    <PhoneLink number="+91 9980944559" />
                  </div>
                </div>
              </motion.div>


              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.1, type: 'spring', stiffness: 100 }}
                className="group relative bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500 bg-opacity-10 rounded-full transition-all duration-700 group-hover:scale-150 group-hover:opacity-20"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-[#00325b] mb-6 transform transition-transform duration-300 group-hover:scale-110">
                    <Mail className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Email Us</h3>
                  <a
                    href="mailto:hospethydraulicsolutions@gmail.com"
                    className="flex flex-col sm:flex-row sm:items-center text-blue-600 hover:text-[#F5A623] transition-colors text-base font-medium group"
                  >
                    <span className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center mb-2 sm:mb-0 sm:mr-3 group-hover:bg-[#F5A623] group-hover:text-white transition-colors flex-shrink-0">
                      <Mail className="w-3.5 h-3.5" />
                    </span>
                    <span className="break-all text-sm lg:text-base">hospethydraulicsolutions@gmail.com</span>
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.2, type: 'spring', stiffness: 100 }}
                className="group relative bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-green-500 bg-opacity-10 rounded-full transition-all duration-700 group-hover:scale-150 group-hover:opacity-20"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-[#00325b] mb-6 transform transition-transform duration-300 group-hover:scale-110">
                    <Clock className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Working Hours</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center mr-3 mt-0.5 group-hover:bg-[#F5A623] group-hover:text-white transition-colors flex-shrink-0">
                        <Clock className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium">Monday - Saturday</p>
                        <p className="text-gray-500 text-sm">9:00 AM - 6:00 PM</p>
                      </div>
                    </div>
                    <div className="flex items-start pt-2">
                      <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center mr-3 mt-0.5 text-red-400 group-hover:bg-red-100 group-hover:text-red-500 transition-colors flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                        </svg>
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium">Sunday</p>
                        <p className="text-gray-500 text-sm">Closed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#00325b] to-[#0a4b8c] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Contact us today to discuss your hydraulic system needs and discover how we can help your business thrive.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:+919980944559"
                className="bg-white text-[#00325b] hover:bg-blue-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-300 flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Us Now
              </a>
              <a
                href="mailto:hospethydraulicsolutions@gmail.com"
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold py-3 px-8 rounded-lg transition-colors duration-300 flex items-center justify-center"
              >
                <Mail className="w-5 h-5 mr-2" />
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;